/**
 * Bespoke isometric line-art pallet stack that "builds" itself board by board.
 * Pure SVG + CSS keyframes (board-snap), no animation libraries.
 *
 * Markup is precomputed once as an HTML string so React does not create ~130
 * polygon VNodes on every render/hydration (main-thread Style & Layout win).
 */

const COS = 0.866;
const SIN = 0.5;

function iso(x: number, y: number, z: number) {
  return [(x - y) * COS, (x + y) * SIN - z] as const;
}

type Board = {
  x: number;
  y: number;
  z: number;
  w: number;
  d: number;
  h: number;
  order: number;
};

function boardFaces(b: Board) {
  const { x, y, z, w, d, h } = b;
  const p = (px: number, py: number, pz: number) => iso(px, py, pz).join(",");
  const top = [p(x, y, z + h), p(x + w, y, z + h), p(x + w, y + d, z + h), p(x, y + d, z + h)].join(
    " ",
  );
  const front = [
    p(x, y + d, z + h),
    p(x + w, y + d, z + h),
    p(x + w, y + d, z),
    p(x, y + d, z),
  ].join(" ");
  const side = [
    p(x + w, y, z + h),
    p(x + w, y + d, z + h),
    p(x + w, y + d, z),
    p(x + w, y, z),
  ].join(" ");
  return { top, front, side };
}

const PALLET_W = 190;
const PALLET_D = 160;
const STRINGER_H = 16;
const BOARD_H = 7;
const LAYER_H = STRINGER_H + BOARD_H + 5;

function buildPallet(layer: number, startOrder: number): Board[] {
  const z = layer * LAYER_H;
  const boards: Board[] = [];
  let order = startOrder;

  [0, (PALLET_W - 26) / 2, PALLET_W - 26].forEach((x) => {
    boards.push({ x, y: 0, z, w: 26, d: PALLET_D, h: BOARD_H, order: order++ });
  });

  [0, (PALLET_D - 20) / 2, PALLET_D - 20].forEach((y) => {
    boards.push({
      x: 0,
      y,
      z: z + BOARD_H,
      w: PALLET_W,
      d: 20,
      h: STRINGER_H,
      order: order++,
    });
  });

  const count = 5;
  for (let i = 0; i < count; i++) {
    const bw = 24;
    const gap = (PALLET_D - count * bw) / (count - 1);
    boards.push({
      x: 0,
      y: i * (bw + gap),
      z: z + BOARD_H + STRINGER_H,
      w: PALLET_W,
      d: bw,
      h: BOARD_H,
      order: order++,
    });
  }

  return boards;
}

function buildSvgInner(): string {
  // Three layers keeps the hero readable while cutting ~25% of SVG paint work.
  const layers = 3;
  const boards: Board[] = [];
  let order = 0;
  for (let l = 0; l < layers; l++) {
    const pallet = buildPallet(l, order);
    order += pallet.length;
    boards.push(...pallet);
  }

  const boardMarkup = boards
    .map((b) => {
      const f = boardFaces(b);
      const delay = 240 + b.order * 46;
      return `<g class="board-snap" style="animation-delay:${delay}ms"><polygon points="${f.front}" fill="var(--charcoal)" fill-opacity="0.35" stroke="var(--bone)" stroke-opacity="0.5" stroke-width="1.1"/><polygon points="${f.side}" fill="var(--charcoal)" fill-opacity="0.5" stroke="var(--bone)" stroke-opacity="0.35" stroke-width="1.1"/><polygon points="${f.top}" fill="url(#deckTop)" stroke="var(--bone)" stroke-opacity="0.8" stroke-width="1.2" stroke-linejoin="round"/></g>`;
    })
    .join("");

  return `<title id="pallet-stack-title">Illustration of a stack of wooden pallets</title><defs><linearGradient id="deckTop" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="var(--amber-brand)" stop-opacity="0.32"/><stop offset="100%" stop-color="var(--amber-deep)" stop-opacity="0.12"/></linearGradient></defs><ellipse cx="0" cy="96" rx="196" ry="52" fill="var(--amber-brand)" opacity="0.07"/>${boardMarkup}`;
}

const PALLET_STACK_INNER = buildSvgInner();

export function PalletStack({ className }: { className?: string }) {
  return (
    <svg
      viewBox="-230 -290 460 400"
      width={460}
      height={400}
      className={className}
      role="img"
      aria-labelledby="pallet-stack-title"
      style={{ contain: "layout style" }}
      dangerouslySetInnerHTML={{ __html: PALLET_STACK_INNER }}
    />
  );
}
